import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { typeIcons, IconLink, locationLabel } from '../components/Utils';
import axios from "../axiosConfig";
import Modal from '../components/Modal';
import '../pages/Job.css';
import PageTitle from "../components/PageTitle";

const Session = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const linkedComponentTooltip = "This component is linked. When used in a programme, any changes to the saved component will be updated in the programme. If this component is changed in a programme it will become unlinked.";
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sessionFormData, setSessionFormData] = useState({
    title: '',
    session_date: '',
    start_time: '',
    end_time: '',
    duration_quantity: '',
    duration_time: '',
    type: '',
    location_id: '',
  });
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [allTrainingLocations, setAllTrainingLocations] = useState([]);
  const [saving, setSaving] = useState(false);

  const location = useLocation();
  const id = new URLSearchParams(location.search).get('id');

  useEffect(() => {
    axios
      .get('/locations/', { withCredentials: true })
      .then((res) => setAllTrainingLocations(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Failed to load locations:', err));
  }, []);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await axios.get(`/sessions/${id}/`, { withCredentials: true });
        const data = response.data;
        console.log("Session data:", data);

        setSession(data);
        setSessionFormData({
          title: data.title || '',
          session_date: data.start_time ? data.start_time.slice(0, 10) : '',
          start_time: data.start_time ? data.start_time.slice(11, 16) : '',
          end_time: data.end_time ? data.end_time.slice(11, 16) : '',
          duration_quantity: data.duration_quantity || '',
          duration_time: data.duration_time || '',
          type: data.type || '',
          location_id: data.location_id ? String(data.location_id) : '',
        });
        setSelectedComponents(data.assigned_component_ids || []);

        const isStaging = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase() === 'staging';
        document.title = `${data.title || 'Session'} - FT Portal${isStaging ? ' (Staging)' : ''}`;

      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [id]);

  useEffect(() => {
    const isStaging = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase() === 'staging';
    return () => {
      document.title = isStaging ? 'FT Portal (Staging)' : 'FT Portal';
    };
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('en-GB');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const handleSessionFormChange = (e) => {
    const { name, value } = e.target;
    setSessionFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleComponentToggle = (componentId) => {
    setSelectedComponents(prev =>
      prev.includes(componentId) ? [] : [componentId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const start_dt = sessionFormData.session_date
        ? `${sessionFormData.session_date} ${sessionFormData.start_time || '00:00'}:00`
        : null;
      const end_dt = sessionFormData.session_date
        ? `${sessionFormData.session_date} ${sessionFormData.end_time || '00:00'}:00`
        : null;
      const payload = {
        title: sessionFormData.title || null,
        start_time: start_dt,
        end_time: end_dt,
        duration_quantity: sessionFormData.duration_quantity || null,
        duration_time: sessionFormData.duration_time || null,
        type: sessionFormData.type || null,
        location_id: sessionFormData.location_id ? parseInt(sessionFormData.location_id, 10) : null,
        component_ids: selectedComponents,
      };

      const response = await axios.patch(`/sessions/${id}/`, payload, { withCredentials: true });

      if (response.data.success) {
        const sessionResponse = await axios.get(`/sessions/${id}/`, { withCredentials: true });
        const updated = sessionResponse.data;
        setSession(updated);
        setSessionFormData({
          title: updated.title || '',
          session_date: updated.start_time ? updated.start_time.slice(0, 10) : '',
          start_time: updated.start_time ? updated.start_time.slice(11, 16) : '',
          end_time: updated.end_time ? updated.end_time.slice(11, 16) : '',
          duration_quantity: updated.duration_quantity || '',
          duration_time: updated.duration_time || '',
          type: updated.type || '',
          location_id: updated.location_id ? String(updated.location_id) : '',
        });
        setSelectedComponents(updated.assigned_component_ids || []);
        setShowEditModal(false);
      }
    } catch (err) {
      console.error('Failed to update session:', err);
      alert('Failed to update session');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/sessions/${id}/`, { withCredentials: true });
      if (session.job_data) {
        window.location.href = `/job?id=${session.job_data.id}`;
      } else {
        window.location.href = '/jobs';
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
      alert('Failed to delete session');
    }
  };

  if (loading) return <div><p>Loading session...</p></div>;
  if (!session) return <div><p>Session not found.</p></div>;

  const clientAccountId = session.job_data?.account_id ?? null;

  return (
    <div className="page-layout">
      <PageTitle>
        {typeIcons['session'] || '📅'} {session.title || `Session #${session.id}`}
      </PageTitle>
      <div className="page-header">
        <div className="page-header-buttons">
          <button onClick={() => setShowEditModal(true)}>Edit Session</button>
          <button onClick={() => setShowDeleteConfirm(true)} className="delete-btn">Delete Session</button>
        </div>
      </div>

      <div className="page-content">
        <h3>Session Details</h3>

        <p><em>ID:</em> {session.id}</p>
        <p><em>Title:</em> {session.title || '—'}</p>

        <p>
          <em>Job:</em>{" "}
          {session.job_data ?
            <Link to={`/job?id=${session.job_data.id}`}>
              {session.job_data.title || `Job #${session.job_data.id}`}
            </Link>
            : session.job_id ? `Job #${session.job_id}` : '—'}
        </p>

        <p><em>Start Time:</em> {formatDate(session.start_time)}</p>
        <p><em>End Time:</em> {formatDate(session.end_time)}</p>

        <p>
          <em>Duration:</em>{" "}
          {session.duration_quantity && session.duration_time ?
            `${session.duration_quantity} ${session.duration_time.toLowerCase()}` : '—'}
        </p>

        <p>
          <em>Tutor:</em>{" "}
          {session.tutor_data ?
            <Link to={`/contact?id=${session.tutor_data.id}`}>
              {session.tutor_data.first_name} {session.tutor_data.last_name}
            </Link>
            : session.tutor_contact_id ? `Contact #${session.tutor_contact_id}` : '—'}
        </p>


        <p><em>Type:</em> {session.type || '—'}</p>

        <p>
          <em>Location:</em>{" "}
          {session.location_data ?
            locationLabel(session.location_data)
            : session.location_id ? `Location #${session.location_id}` : '—'}
        </p>

        <p>
          <em>Virtual Link:</em>{" "}
          {session.virtual_link ?
            <a href={session.virtual_link} target="_blank" rel="noopener noreferrer">
              {session.virtual_link}
            </a>
            : '—'}
        </p>

        {session.attendees && session.attendees.length > 0 && (
          <>
            <h3>Attendees</h3>
            <ul>
              {session.attendees.map((attendee, index) => (
                <li key={index}>
                  Attendee #{attendee.id} - Contact: {attendee.contact_id || 'N/A'}
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Edit Session Modal */}
        <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Session">
          <div className="form">
            <label>
              Title (optional):
              <input
                autoComplete="off"
                type="text"
                name="title"
                value={sessionFormData.title}
                onChange={handleSessionFormChange}
                placeholder="Session title..."
              />
            </label>

            <label>
              Date:
              <input
                autoComplete="off"
                type="date"
                name="session_date"
                value={sessionFormData.session_date}
                onChange={handleSessionFormChange}
              />
            </label>

            <label>
              Start Time:
              <input
                autoComplete="off"
                type="time"
                name="start_time"
                value={sessionFormData.start_time}
                onChange={handleSessionFormChange}
              />
            </label>

            <label>
              End Time:
              <input
                autoComplete="off"
                type="time"
                name="end_time"
                value={sessionFormData.end_time}
                onChange={handleSessionFormChange}
              />
            </label>

            <label>
              Duration:
              <div>
                <select
                  autoComplete="off"
                  name="duration_quantity"
                  value={sessionFormData.duration_quantity}
                  onChange={handleSessionFormChange}
                  style={{ flex: 1 }}
                  disabled={!sessionFormData.duration_time}
                >
                  <option value="">—</option>
                  {sessionFormData.duration_time === 'Hour' && (
                    <>
                      <option value="1">1</option>
                      <option value="1.5">1.5</option>
                      <option value="2">2</option>
                      <option value="2.5">2.5</option>
                      <option value="3">3</option>
                      <option value="3.5">3.5</option>
                      <option value="4">4</option>
                      <option value="8">8</option>
                    </>
                  )}
                  {sessionFormData.duration_time === 'Day' && (
                    <>
                      <option value="0.5">0.5</option>
                      <option value="1">1</option>
                    </>
                  )}
                </select>
                <select
                  autoComplete="off"
                  name="duration_time"
                  value={sessionFormData.duration_time}
                  onChange={handleSessionFormChange}
                  style={{ flex: 1 }}
                >
                  <option value="">Select unit...</option>
                  <option value="Hour">Hour(s)</option>
                  <option value="Day">Day(s)</option>
                </select>
              </div>
            </label>

            <label>
              Type:
              <select
                autoComplete="off"
                name="type"
                value={sessionFormData.type}
                onChange={handleSessionFormChange}
              >
                <option value="">Select type...</option>
                <option value="In-Person">In-Person</option>
                <option value="Virtual">Virtual</option>
              </select>
            </label>

            <label>
              Training Location:
              <select
                autoComplete="off"
                name="location_id"
                value={sessionFormData.location_id}
                onChange={handleSessionFormChange}
              >
                <option value="">No location</option>
                {clientAccountId &&
                  allTrainingLocations.filter(l => l.account_id === clientAccountId).length > 0 && (
                    <optgroup label="Client Account">
                      {allTrainingLocations
                        .filter(l => l.account_id === clientAccountId)
                        .map(loc => (
                          <option key={loc.id} value={String(loc.id)}>
                            {locationLabel(loc)}
                          </option>
                        ))}
                    </optgroup>
                  )}
                {allTrainingLocations.filter(l => l.venue && l.account_id !== clientAccountId).length > 0 && (
                  <optgroup label="Venues">
                    {allTrainingLocations
                      .filter(l => l.venue && l.account_id !== clientAccountId)
                      .map(loc => (
                        <option key={loc.id} value={String(loc.id)}>
                          {locationLabel(loc)} ({loc.account_name || ''})
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </label>

            <div>
              <label>Programme Component:</label>
              <div className="component-list">
                {session?.programme_content && session.programme_content.length > 0 ? (
                  <>
                    <label className="component-item component-label" style={{ cursor: 'pointer' }}>
                      <input
                        autoComplete="off"
                        type="radio"
                        name="session-component"
                        checked={selectedComponents.length === 0}
                        onChange={() => setSelectedComponents([])}
                      />
                      <span className="component-text">None</span>
                    </label>
                    {session.programme_content.map((component) => (
                      <label
                        key={component.component_id}
                        className="component-item component-label"
                        style={{ cursor: 'pointer' }}
                      >
                        <input
                          autoComplete="off"
                          type="radio"
                          name="session-component"
                          checked={selectedComponents.includes(component.component_id)}
                          onChange={() => setSelectedComponents([component.component_id])}
                        />
                        <span className="component-text">
                          {component.type === 'module' && 'Module: '}
                          {component.type === 'block' && 'Block: '}
                          {component.type === 'element' && 'Slide: '}
                          {component.name || component.slide_name || `${component.type} #${component.component_id}`}
                          {(component.type === 'module' || component.type === 'block') && component.linked && (
                            <IconLink
                              aria-label="Linked"
                              title={linkedComponentTooltip}
                              style={{ marginLeft: 6, verticalAlign: 'middle' }}
                            />
                          )}
                          {component.type === 'block' && component.duration && ` (${component.duration} min)`}
                        </span>
                      </label>
                    ))}
                  </>
                ) : (
                  <div className="component-empty">
                    {session?.programme_data ? 'No components in this programme' : 'No programme assigned to this job'}
                  </div>
                )}
              </div>
            </div>

            <div>
              <button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={() => setShowEditModal(false)}>Cancel</button>
            </div>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Confirm Delete Session">
          <p>Are you sure you want to delete <strong>{session.title || `Session #${session.id}`}</strong>? This action cannot be undone.</p>
          <div>
            <button onClick={handleDelete} style={{ color: 'red' }}>Yes, Delete</button>
            <button onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Session;
